// Components/Layout/SidebarLayout.jsx
import React, { useState, useEffect } from "react";
import {
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Toolbar,
  Box,
  CssBaseline,
  AppBar,
  Collapse,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import CheckCircle from "@mui/icons-material/CheckCircle";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import { getSidebarItems } from "../../data/SidebarItems";
import logo from "../../Assets/images/ConnectHealthPro.png";
import ProfileCompletionStatus from "../UI/ProfileCompletionStatus";

const drawerWidth = 240;

const SidebarLayout = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const location = useLocation();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMenus, setOpenMenus] = useState({});
  const [sidebarItems, setSidebarItems] = useState(getSidebarItems());

  useEffect(() => {
    const updateSidebar = () => {
      setSidebarItems(getSidebarItems());
    };

    window.addEventListener("storage", updateSidebar);
    window.addEventListener("triviaCompleted", updateSidebar);
    window.addEventListener("triviaFetched", updateSidebar);

    updateSidebar();
    const timeout = setTimeout(updateSidebar, 300);

    return () => {
      window.removeEventListener("storage", updateSidebar);
      window.removeEventListener("triviaCompleted", updateSidebar);
      window.removeEventListener("triviaFetched", updateSidebar);
      clearTimeout(timeout);
    };
  }, []);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  const handleLogout = () => {
    localStorage.removeItem("USER_INFO");
    localStorage.clear();
    sessionStorage.clear();
    navigate("/login");
  };

  const toggleMenu = (label) => {
    setOpenMenus((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const renderListItem = (item) => {
    const { label, path, icon, children, isExternal } = item;
    const isSelected = location.pathname === path;
    const isOpen = openMenus[label];

    // Special handling for Health Scenarios
    const isTrivia = label === "Health Scenarios";
    const today = new Date().toDateString();
    const triviaCompletedToday = localStorage.getItem("triviaCompleted") === today;
    const isTriviaDisabled = isTrivia && triviaCompletedToday;
    const showNewBadge = isTrivia && item.badge === "new" && !triviaCompletedToday;

    if (children) {
      return (
        <React.Fragment key={label}>
          <ListItem
            button
            onClick={() => toggleMenu(label)}
            sx={{
              backgroundColor: isOpen ? "#f4f6f8" : "transparent",
              "&:hover": { backgroundColor: "#e0f2f1" },
            }}
          >
            <ListItemIcon>{icon}</ListItemIcon>
            <ListItemText primary={label} />
            {isOpen ? <ExpandLess /> : <ExpandMore />}
          </ListItem>

          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding sx={{ pl: 4 }}>
              {children.map(renderListItem)}
            </List>
          </Collapse>
        </React.Fragment>
      );
    }

    // External link
    if (isExternal) {
      return (
        <ListItem
          button
          component="a"
          href={path}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            "&:hover": { backgroundColor: "#e0f2f1" },
          }}
        >
          <ListItemIcon>{icon}</ListItemIcon>
          <ListItemText primary={label} />
        </ListItem>
      );
    }

    // Normal navigation item (including logout & trivia)
    return (
      <ListItem
        button
        key={label}
        selected={isSelected}
        disabled={isTriviaDisabled}
        onClick={() => {
          if (isTriviaDisabled) return;
          if (label === "Log Out" || path === "/logout") {
            handleLogout();
          } else {
            navigate(path);
          }
        }}
        sx={{
          color: isSelected ? "primary.main" : "text.primary",
          backgroundColor: isSelected ? "#f4f6f8" : "transparent",
          "&:hover": {
            backgroundColor: isTriviaDisabled ? "transparent" : "#e0f2f1",
          },
          cursor: isTriviaDisabled ? "not-allowed" : "pointer",
          opacity: isTriviaDisabled ? 0.6 : 1,
          position: "relative",
        }}
        title={isTriviaDisabled ? "Completed for today" : ""}
      >
        <ListItemIcon>{icon}</ListItemIcon>
        <ListItemText primary={label} />

        {/* New trivia pulsing dot */}
        {showNewBadge && (
          <div
            style={{
              position: "absolute",
              right: "16px",
              width: "8px",
              height: "8px",
              backgroundColor: "#2e7d32",
              borderRadius: "50%",
              animation: "pulse 2s infinite",
            }}
            title="New Scenarios available"
          />
        )}

        {/* Completed checkmark */}
        {isTrivia && triviaCompletedToday && (
          <CheckCircle
            sx={{
              position: "absolute",
              right: "16px",
              color: "#4caf50",
              opacity: 0.9,
            }}
            title="Completed for today"
          />
        )}
      </ListItem>
    );
  };

  const drawerContent = (
    <div>
      <Toolbar>
        <img src={logo} alt="ConnectHealthPro" style={{ width: "100%" }} />
      </Toolbar>

      <Box sx={{ px: 2, py: 1 }}>
        <ProfileCompletionStatus />
      </Box>

      <List>{sidebarItems.map(renderListItem)}</List>
    </div>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <CssBaseline />

      <AppBar position="fixed" sx={{ zIndex: theme.zIndex.drawer + 1 }}>
        <Toolbar sx={{ pl: { md: `${drawerWidth}px` } }}>
          {isMobile && (
            <IconButton
              color="inherit"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Header />
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
        <Drawer
          variant={isMobile ? "temporary" : "permanent"}
          open={isMobile ? mobileOpen : true}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              boxSizing: "border-box",
              pt: "14px",
              zIndex: 10000,
            },
          }}
        >
          {drawerContent}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          mt: "64px",
        }}
        className="content"
      >
        <Outlet />
      </Box>

      <Box
        component="footer"
        sx={{
          p: 2,
          pl: { md: `${drawerWidth}px` },
          bgcolor: "grey.100",
          mt: "auto",
        }}
      >
        <Footer />
      </Box>
    </Box>
  );
};

export default SidebarLayout;